import { useRef, useState, ChangeEvent, DragEvent } from 'react'
import { Control, Controller, useWatch, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Upload, X, Loader2, FileText } from 'lucide-react'
import toast from 'react-hot-toast'

import { Switch } from '@/components/ui/switch'
import useUploadFile from '@/hooks/useUploadFile'
import { Contract, NDAAttachment } from '@/modules/Contract/types'
import { cn } from '@/lib/utils'

const STORAGE_URL = import.meta.env.APP_STORAGE || ''
const ACCEPTED_TYPES = [
    '.pdf',
    '.doc',
    '.docx',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]

interface Props {
    control: Control<Contract>
    setValue: UseFormSetValue<Contract>
}

const NDAConfigSection: React.FC<Props> = ({ control, setValue }) => {
    const { t } = useTranslation()
    const inputRef = useRef<HTMLInputElement>(null)
    const [uploading, setUploading] = useState(false)
    const [isDragging, setIsDragging] = useState(false)

    const ndaEnabled = useWatch({ control, name: 'nda_enable' })
    const attachments = useWatch({ control, name: 'nda_attachments' }) as
        | NDAAttachment[]
        | undefined

    const { uploadAsync, isUploading, percent } = useUploadFile({
        prefix: 'contract-nda',
        onSuccess: (results) => {
            const newAtts: NDAAttachment[] = results
                .filter(
                    (r) =>
                        r.status === 'fulfilled' && r.value.attachment_file_url
                )
                .map((r) => ({
                    file: new File([], r.value.attachment_file_name || 'file'),
                    url: `${STORAGE_URL}/${r.value.attachment_file_url}`,
                }))
            setValue('nda_attachments', [
                ...(attachments || []),
                ...newAtts,
            ] as unknown as NDAAttachment[])
            toast.success(t('nda_upload_success'))
            setUploading(false)
        },
        onError: () => {
            toast.error(t('nda_upload_failed'))
            setUploading(false)
        },
    })

    const handleFiles = async (files: File[]) => {
        const validFiles = files.filter((file) => {
            const ext = '.' + file.name.split('.').pop()?.toLowerCase()
            return (
                ACCEPTED_TYPES.includes(ext) ||
                ACCEPTED_TYPES.includes(file.type)
            )
        })

        if (validFiles.length === 0) {
            toast.error(t('nda_file_type_error'))
            return
        }

        if (validFiles.length < files.length) {
            toast.error(
                t('nda_file_invalid_skipped', {
                    count: files.length - validFiles.length,
                })
            )
        }

        setUploading(true)
        await uploadAsync(validFiles)
    }

    const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files?.length) return
        await handleFiles(Array.from(e.target.files))
        if (inputRef.current) inputRef.current.value = ''
    }

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        e.stopPropagation()
        if (ndaEnabled && !uploading && !isUploading) {
            setIsDragging(true)
        }
    }

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(false)
    }

    const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(false)

        if (!ndaEnabled || uploading || isUploading) return

        const files = Array.from(e.dataTransfer.files)
        if (files.length > 0) {
            await handleFiles(files)
        }
    }

    const removeAtt = (i: number) => {
        setValue(
            'nda_attachments',
            (attachments || []).filter(
                (_, idx) => idx !== i
            ) as unknown as NDAAttachment[]
        )
    }

    return (
        <div
            data-tour="nda-config-section"
            className="border border-purple-200 bg-purple-50/30 rounded-md overflow-hidden"
        >
            {/* Header */}
            <div className="flex items-center gap-2 p-3">
                <Controller
                    name="nda_enable"
                    control={control}
                    render={({ field }) => (
                        <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            className="scale-75"
                        />
                    )}
                />
                <FileText className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-medium text-purple-900">NDA</span>
            </div>

            {/* Content - Dropzone */}
            <div className="border-t border-purple-200 p-3 bg-white/50">
                {/* Dropzone */}
                <div
                    className={cn(
                        'border-2 border-dashed rounded-lg p-4 text-center transition-all cursor-pointer',
                        isDragging
                            ? 'border-purple-500 bg-purple-100/50'
                            : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50/30',
                        !ndaEnabled && 'opacity-50 cursor-not-allowed'
                    )}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => ndaEnabled && inputRef.current?.click()}
                >
                    {uploading || isUploading ? (
                        <div className="flex flex-col items-center gap-2">
                            <Loader2 className="w-6 h-6 text-purple-500 animate-spin" />
                            <span className="text-xs text-purple-600">
                                {Object.values(percent)[0] || 0}%
                            </span>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center gap-1">
                            <Upload
                                className={cn(
                                    'w-6 h-6',
                                    isDragging
                                        ? 'text-purple-600 animate-bounce'
                                        : 'text-gray-400'
                                )}
                            />
                            <span className="text-xs text-gray-500">
                                {isDragging
                                    ? t('nda_drop_here')
                                    : t('nda_drag_or_click')}
                            </span>
                            <span className="text-[10px] text-gray-400">
                                {t('nda_accepted_types')}
                            </span>
                        </div>
                    )}
                </div>

                {/* Uploaded files */}
                {attachments && attachments.length > 0 && (
                    <div className="space-y-2 mt-2">
                        {attachments.map((att, i) => (
                            <div
                                key={i}
                                className="flex items-center justify-between gap-1 bg-white border border-primary rounded px-2 py-1"
                            >
                                <div className="flex items-center gap-1">
                                    <FileText className="w-3 h-3 text-gray-500" />
                                    <span className="text-xs text-gray-500 truncate text-primary">
                                        {att.url?.split('/').pop()}
                                    </span>
                                </div>
                                {ndaEnabled && (
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            removeAtt(i)
                                        }}
                                        className="text-gray-400 hover:text-red-500"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                <input
                    ref={inputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    multiple
                    className="hidden"
                    onChange={handleFileChange}
                />
            </div>
        </div>
    )
}

export default NDAConfigSection
